/**
 * Light or dark.
 *
 * The interface follows the system until the person presses the toggle; from
 * then on their choice is kept in this browser (`hg.cc.theme`, not a secret).
 * The theme is one attribute on <html>, `data-theme`, which the tokens in
 * src/styles/tailwind.css switch on. index.html sets it before the first
 * paint with the same rule, so a dark-theme night shift never sees a white
 * flash.
 */
import { useSyncExternalStore } from "react";

export type Theme = "light" | "dark";

export const THEME_KEY = "hg.cc.theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

const listeners = new Set<() => void>();

function stored(): Theme | null {
  try {
    const value = localStorage.getItem(THEME_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    // Storage is refused (private mode, a locked-down browser): follow the system.
    return null;
  }
}

function systemTheme(): Theme {
  return typeof matchMedia === "function" && matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

/** What is shown now: the choice, or the system's when none was made. */
export function currentTheme(): Theme {
  return stored() ?? systemTheme();
}

function apply(): void {
  document.documentElement.setAttribute("data-theme", currentTheme());
  for (const listener of listeners) listener();
}

/** The person's choice, remembered. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Not remembered, then; still applied for this page.
    document.documentElement.setAttribute("data-theme", theme);
    for (const listener of listeners) listener();
    return;
  }
  apply();
}

export function toggleTheme(): void {
  setTheme(currentTheme() === "dark" ? "light" : "dark");
}

/** Call once at start: applies the theme and follows the system and other tabs. */
export function initTheme(): void {
  apply();
  if (typeof matchMedia === "function") {
    matchMedia(DARK_QUERY).addEventListener("change", apply);
  }
  window.addEventListener("storage", (event) => {
    if (event.key === THEME_KEY) apply();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, currentTheme);
}
